import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, Clock, Route, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useDealerManagementSimple } from '@/hooks/useDealerManagementSimple';
import { useRoutes } from '../hooks/useTechnicianRoutes';
import TechnicianCard from './TechnicianCard';
import RouteDetailsPanel from './RouteDetailsPanel';
import { printDealerRecallSummary } from '../utils/printRoutes';
import { logUserActivity } from '@/lib/activity/userActivity';
import { getStopLocationKey, isCustomerStop } from '@/lib/manualStops/locationKey';
import { DealerLocation, Technician, RouteStop, TechnicianRoute } from '../types';

/**
 * Stop identity: business stops by dealer name + city (as before); customer stops by
 * household, since every customer stop is named 'Customer stop'. Works on staged rows
 * and on stored route_data stops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped staged/stored rows
const confirmStopKey = (stop: any): string =>
  isCustomerStop(stop)
    ? getStopLocationKey({ ...stop, dealer_address: stop.dealer_address ?? stop.dealer_street })
    : `${stop.dealer_name}-${stop.dealer_city || ''}`;

interface Assignment {
  id: string;
  technician_id: string;
  vin: string;
  campaign_number: string;
  recall_title?: string;
  assignment_status: 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  dealer_name?: string;
  expected_visit_date?: string;
}

interface TechnicianData {
  id: string;
  full_name: string;
  phone_number?: string;
  email?: string;
  skill_level: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const ConfirmRoutesStep: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { selectedRecalls } = useSelectedRecalls();
  const { assignments, activeTechnicians } = useTechnicianAssignments();

  // Get userId from session, or use a default for development
  const userId = session?.user?.id || 'dev-user-' + Date.now();
  const { baseLocation } = useDealerManagementSimple(userId);

  // Get date range from assignments for fetching stored routes
  const dateRange = useMemo(() => {
    const dates = assignments.map(a => a.expected_visit_date).filter((d): d is string => !!d);
    if (dates.length === 0) {
      const today = new Date().toISOString().split('T')[0];
      return { startDate: today, endDate: today };
    }
    const sortedDates = [...dates].sort();
    return {
      startDate: sortedDates[0],
      endDate: sortedDates[sortedDates.length - 1]
    };
  }, [assignments]);

  // Fetch stored routes from technician_routes table (single source of truth)
  const { data: storedRoutes } = useRoutes({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    enabled: assignments.length > 0
  });

  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);

  // Build routes from assignments
  const buildRoutesFromAssignments = (): TechnicianRoute[] => {
    if (assignments.length === 0 || activeTechnicians.length === 0) {
      return [];
    }

    // Group assignments by technician AND expected_visit_date
    // This ensures that same technician on different dates = different routes
    const assignmentsByTechnicianAndDate = assignments.reduce(
      (acc, assignment) => {
        const dateKey = assignment.expected_visit_date || 'no-date';
        const groupKey = `${assignment.technician_id}_${dateKey}`;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            technician_id: assignment.technician_id,
            expected_visit_date: assignment.expected_visit_date || undefined,
            assignments: []
          };
        }
        acc[groupKey]!.assignments.push(assignment);
        return acc;
      },
      {} as Record<
        string,
        {
          technician_id: string;
          expected_visit_date?: string | undefined;
          assignments: Assignment[];
        }
      >
    );

    // Convert grouped data to array for processing
    const groupedAssignments = Object.values(assignmentsByTechnicianAndDate);

    // Build routes from grouped assignments
    const routes = groupedAssignments
      .map((group, index) => {
        const technician = activeTechnicians.find(
          (t: TechnicianData) => t.id === group.technician_id
        );
        if (!technician) return null;

        const technicianAssignments = group.assignments;

        // Find matching stored route from single source of truth (technician_routes table)
        const matchingStoredRoute = storedRoutes?.find(
          (r: any) =>
            r.technician_id === group.technician_id && r.route_date === group.expected_visit_date
        );

        // Map each location to its stored stop_order for preserving route sequence
        const storedStopOrderMap = new Map<string, number>();
        if (matchingStoredRoute?.route_data?.stops) {
          matchingStoredRoute.route_data.stops.forEach((stop: any) => {
            storedStopOrderMap.set(confirmStopKey(stop), stop.stop_order);
          });
        }

        // One stop per location, holding every recall assigned there
        const stopsByKey = new Map<string, { dealer: DealerLocation; recalls: any[] }>();
        technicianAssignments.forEach((assignment: Assignment) => {
          const recall = selectedRecalls.find(
            r => r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
          );
          if (!recall) return;

          const key = confirmStopKey(recall);
          const existing = stopsByKey.get(key);
          if (existing) {
            existing.recalls.push(recall);
            return;
          }

          // Use fallback coordinates if dealer coordinates are missing
          let latitude = recall.dealer_latitude;
          let longitude = recall.dealer_longitude;

          // If coordinates are missing, use a fallback based on city/region
          if (!latitude || !longitude || latitude === 0 || longitude === 0) {
            // Use base location coordinates as fallback
            if (baseLocation?.coordinates) {
              latitude = baseLocation.coordinates.lat;
              longitude = baseLocation.coordinates.lng;
            } else {
              // Use Orlando, FL as final fallback
              latitude = 28.5383;
              longitude = -81.3792;
            }
          }

          stopsByKey.set(key, {
            dealer: {
              id: key,
              name: recall.dealer_name,
              address: recall.dealer_address ?? recall.dealer_street,
              city: recall.dealer_city,
              region: recall.dealer_region,
              postal_code: recall.dealer_postal_code,
              phone: recall.dealer_phone,
              latitude: latitude,
              longitude: longitude,
              recall_count: 1,
              skill_level: 2 // Default skill level
            } as DealerLocation,
            recalls: [recall]
          });
        });

        // Only create route if there are valid dealer locations
        if (stopsByKey.size === 0) {
          return null;
        }

        // Create route stops with stop_order from stored route (single source of truth)
        const stops = Array.from(stopsByKey, ([key, { dealer, recalls }], stopIndex) => {
          // Use stored stop_order if available, otherwise fall back to sequential
          const storedOrder = storedStopOrderMap.get(key);
          const stopOrder = storedOrder !== undefined ? storedOrder : stopIndex + 1;
          const first = recalls[0];

          return {
            id: `${group.technician_id}-${dealer.id}`,
            dealer,
            recalls,
            arrival_time: '09:00', // Placeholder
            departure_time: '10:00', // Placeholder
            service_duration: 60, // Placeholder
            stop_order: stopOrder,
            // Lets the printout give each household its own page and contact details
            ...(isCustomerStop(first) && {
              stop_kind: 'customer',
              contact_id: first.contact_id ?? null,
              address_line2: first.address_line2 ?? null
            })
          } as RouteStop;
        });

        // Sort stops by stop_order to reflect the stored route sequence
        stops.sort((a, b) => a.stop_order - b.stop_order);

        // Calculate totals (placeholder values)
        const totalDistance = stops.length * 10; // 10 miles per stop
        const totalDuration = stops.length * 60; // 60 minutes per stop

        // Route colors
        const routeColors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];

        // Create unique route ID that includes date
        const dateKey = group.expected_visit_date || 'no-date';
        const routeId = `${group.technician_id}_${dateKey}`;

        return {
          technician_id: routeId, // Use unique ID that includes date
          technician: {
            id: technician.id,
            full_name: technician.full_name,
            phone_number: technician.phone_number || '',
            email: technician.email || '',
            skill_level: technician.skill_level,
            /* eslint-disable indent */
            home_base: baseLocation?.coordinates
              ? {
                  latitude: baseLocation.coordinates.lat,
                  longitude: baseLocation.coordinates.lng
                }
              : {
                  latitude: 28.5383, // Orlando coordinates as fallback
                  longitude: -81.3792
                }
            /* eslint-enable indent */
          } as Technician,
          stops,
          total_distance: totalDistance,
          total_duration: totalDuration,
          route_color: routeColors[index % routeColors.length],
          // Store the actual technician ID and date for confirmation logic
          _actualTechnicianId: group.technician_id,
          _expectedVisitDate: group.expected_visit_date
        } as any;
      })
      .filter((route): route is TechnicianRoute => route !== null);

    return routes;
  };

  const allRoutes = buildRoutesFromAssignments();

  // Filter to only show routes with actual assignments (stops > 0)
  const routes = useMemo(() => {
    return allRoutes.filter(route => route.stops.length > 0);
  }, [allRoutes]);

  // Set first technician as selected by default
  useEffect(() => {
    if (routes.length > 0 && !selectedRouteId && routes[0]) {
      setSelectedRouteId(routes[0].technician_id);
    }
  }, [routes, selectedRouteId]);

  const handleRouteSelect = (route: TechnicianRoute) => {
    setSelectedRouteId(route.technician_id);
  };

  const handlePrintRoute = (route: TechnicianRoute) => {
    printDealerRecallSummary(route);
    logUserActivity({
      category: 'printing',
      action: 'print_dealer',
      entityType: 'route_sheet',
      entityId: (route as any)._actualTechnicianId || route.technician_id,
      entityName: route.technician?.full_name,
      itemCount: route.stops?.length,
      metadata: { source: 'confirm_routes_step' }
    });
  };

  if (assignments.length === 0) {
    return (
      <Card>
        <CardContent className='p-6 text-center'>
          <Users className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
          <h3 className='text-lg font-medium text-foreground mb-2'>No Assignments to Confirm</h3>
          <p className='text-muted-foreground'>
            Go back to Step 1 to assign technicians to recalls before confirming routes.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='w-full h-full space-y-4 lg:space-y-6'>
      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Routes</p>
                <p className='text-2xl font-bold text-foreground'>{routes.length}</p>
              </div>
              <Route className='w-8 h-8 text-blue-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Stops</p>
                <p className='text-2xl font-bold text-foreground'>
                  {routes.reduce((sum, route) => sum + route.stops.length, 0)}
                </p>
              </div>
              <MapPin className='w-8 h-8 text-green-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Distance</p>
                <p className='text-2xl font-bold text-foreground'>
                  {routes.reduce((sum, route) => sum + route.total_distance, 0)} mi
                </p>
              </div>
              <MapPin className='w-8 h-8 text-purple-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Duration</p>
                <p className='text-2xl font-bold text-foreground'>
                  {Math.round(routes.reduce((sum, route) => sum + route.total_duration, 0) / 60)}h
                </p>
              </div>
              <Clock className='w-8 h-8 text-orange-500' />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className='flex gap-4 min-h-0 h-[1200px] lg:h-[1400px]'>
        {/* Left Column - Technician Cards */}
        <div className='w-80 flex-shrink-0'>
          <div className='h-full overflow-y-auto'>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-2'>
                <Users className='w-5 h-5 text-blue-600' />
                <h3 className='text-lg font-semibold'>Technicians</h3>
                <Badge variant='outline' className='ml-2'>
                  {routes.length} assigned
                </Badge>
              </div>
            </div>

            {routes.length > 0 ? (
              <div className='space-y-3'>
                {routes.map(route => (
                  <TechnicianCard
                    key={route.technician_id}
                    route={route}
                    isSelected={selectedRouteId === route.technician_id}
                    onSelect={handleRouteSelect}
                    onPrintRoute={handlePrintRoute}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className='p-6 text-center'>
                  <Users className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
                  <h4 className='text-lg font-medium text-foreground mb-2'>
                    No Assigned Technicians
                  </h4>
                  <p className='text-muted-foreground'>
                    Go back to assign technicians to recalls before confirming routes.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Right Column - Route Details and Map */}
        <div className='flex-1 min-w-0'>
          <RouteDetailsPanel
            selectedRoute={routes.find(route => route.technician_id === selectedRouteId) || null}
            /* eslint-disable indent */
            baseLocation={
              baseLocation
                ? {
                    lat:
                      baseLocation.coordinates?.lat && baseLocation.coordinates.lat !== 0
                        ? baseLocation.coordinates.lat
                        : 28.0589, // Tampa, FL default latitude
                    lng:
                      baseLocation.coordinates?.lng && baseLocation.coordinates.lng !== 0
                        ? baseLocation.coordinates.lng
                        : -82.4126, // Tampa, FL default longitude
                    dealerName: baseLocation.dealerName,
                    address: `${baseLocation.address}, ${baseLocation.city}, ${baseLocation.state}`
                  }
                : {
                    lat: 28.0589, // Tampa, FL default latitude
                    lng: -82.4126, // Tampa, FL default longitude
                    dealerName: 'Default Location',
                    address: 'Tampa, FL'
                  }
            }
            /* eslint-enable indent */
          />
        </div>
      </div>

      {/* Route Actions */}
      <Card>
        <CardContent className='p-6'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <Route className='w-6 h-6 text-blue-500' />
              <div>
                <h3 className='font-medium text-foreground'>Review Routes</h3>
                <p className='text-sm text-muted-foreground'>
                  Review the routes above. Routes will automatically become active on their
                  scheduled date.
                </p>
                <div className='text-xs text-muted-foreground mt-1'>
                  {routes.length} route{routes.length !== 1 ? 's' : ''} ready
                </div>
              </div>
            </div>
            {routes.length > 0 && (
              <Button
                onClick={() => navigate('/routing/preview')}
                size='lg'
                className='flex items-center gap-2'
              >
                <Eye className='w-5 h-5' />
                Preview Routes for Print
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmRoutesStep;
